/* BP-011 — Tasks & Action Management tab (lean v1.0).

   Choice 4a: Property Workspace tab only. Global page deferred to v1.1.
   Choice 5a: Workspace-scoped (no client-side RBAC).

   Layout:
     • Inline create form  — title + priority + due date
     • Task list           — grouped by state
     • Per-task actions    — Start, Complete, Block, Cancel, Delete
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Plus, CheckCircle2, PlayCircle, PauseCircle, XCircle,
  Trash2, CheckSquare, Clock, AlertOctagon,
} from "lucide-react";

const INP = "w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";

const STATES = ["pending", "in_progress", "blocked", "completed", "cancelled"];
const STATE_LABELS = {
  pending: "Pending", in_progress: "In Progress", blocked: "Blocked",
  completed: "Completed", cancelled: "Cancelled",
};

const PRIORITY_STYLES = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high:     "bg-orange-100 text-orange-700 border-orange-200",
  medium:   "bg-amber-100 text-amber-700 border-amber-200",
  low:      "bg-slate-100 text-slate-600 border-slate-200",
};

export default function TasksTab({ property }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    title: "", description: "", priority: "medium", due_at: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/properties/${property.id}/tasks`);
      setTasks(data.items || []);
    } catch { toast.error("Failed to load tasks"); }
    finally { setLoading(false); }
  }, [property.id]);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async () => {
    if (!draft.title.trim()) { toast.error("Title required."); return; }
    setCreating(true);
    try {
      await api.post("/v3/tasks", {
        title: draft.title, description: draft.description || undefined,
        priority: draft.priority, property_id: property.id,
        due_at: draft.due_at ? new Date(draft.due_at).toISOString() : undefined,
      });
      toast.success("Task created");
      setDraft({ title: "", description: "", priority: "medium", due_at: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  }, [draft, property.id, load]);

  const transition = useCallback(async (task, to_state, note) => {
    try {
      await api.post(`/v3/tasks/${task.id}/transition`, { to_state, note });
      toast.success(`${task.title} → ${STATE_LABELS[to_state]}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transition failed");
    }
  }, [load]);

  const del = useCallback(async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await api.delete(`/v3/tasks/${task.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of STATES) g[s] = [];
    for (const t of tasks) (g[t.state] || (g[t.state] = [])).push(t);
    return g;
  }, [tasks]);

  const openCount = grouped.pending.length + grouped.in_progress.length + grouped.blocked.length;
  const overdueCount = tasks.filter((t) => t.is_overdue).length;

  if (loading) return (
    <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="tasks-loading">
      <Loader2 size={14} className="animate-spin"/> Loading tasks…
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-panel-tasks">
      {/* Header + summary */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
            <CheckSquare size={12}/> Tasks · {openCount} open · {grouped.completed.length} completed
          </div>
          {overdueCount > 0 && (
            <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700"
                 data-testid="tasks-overdue-count">
              <AlertOctagon size={12}/> {overdueCount} overdue
            </div>
          )}
        </div>

        {/* Inline create */}
        <div className="mt-3 grid gap-2 sm:grid-cols-6">
          <div className="sm:col-span-3">
            <input value={draft.title}
                   onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                   placeholder="Add a task…"
                   data-testid="tasks-new-title"
                   className={INP}/>
          </div>
          <select value={draft.priority}
                  onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
                  data-testid="tasks-new-priority"
                  className={INP}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <input type="date" value={draft.due_at}
                 onChange={(e) => setDraft({ ...draft, due_at: e.target.value })}
                 data-testid="tasks-new-due"
                 className={INP}/>
          <button onClick={create} disabled={creating}
                  data-testid="tasks-new-create"
                  className="inline-flex items-center justify-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {creating ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>}
            Add
          </button>
        </div>
      </div>

      {/* Grouped task lists */}
      {STATES.map((state) => {
        const list = grouped[state];
        if (!list || list.length === 0) return null;
        const isTerminal = state === "completed" || state === "cancelled";
        return (
          <div key={state} className="rounded-xl border border-slate-200 bg-white p-4"
               data-testid={`tasks-group-${state}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
              {STATE_LABELS[state]} · {list.length}
            </div>
            <div className="space-y-1">
              {list.map((t) => (
                <div key={t.id}
                     className={`flex items-center justify-between gap-3 rounded-md px-2 py-1.5 border ${t.is_overdue ? "border-red-200 bg-red-50/40" : "border-transparent hover:bg-slate-50"}`}
                     data-testid={`tasks-row-${t.id.slice(0,8)}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-block text-[9px] font-semibold uppercase tracking-wider rounded-full border px-1.5 py-0.5 ${PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.medium}`}>
                        {t.priority}
                      </span>
                      <span className={`text-sm truncate ${isTerminal ? "text-slate-500 line-through" : "text-slate-900"}`}>
                        {t.title}
                      </span>
                      {t.is_overdue && (
                        <span className="text-[9px] font-semibold text-red-700 uppercase">OVERDUE</span>
                      )}
                    </div>
                    {t.description && (
                      <div className="text-[11px] text-slate-500 truncate">{t.description}</div>
                    )}
                    {t.due_at && (
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        <Clock size={10} className="inline mr-0.5"/> Due {new Date(t.due_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {state === "pending" && (
                      <ActionBtn testid={`tasks-start-${t.id.slice(0,8)}`}
                                 onClick={() => transition(t, "in_progress")}
                                 icon={<PlayCircle size={12}/>} label="Start"/>
                    )}
                    {state === "in_progress" && (
                      <ActionBtn testid={`tasks-complete-${t.id.slice(0,8)}`}
                                 onClick={() => transition(t, "completed")}
                                 icon={<CheckCircle2 size={12}/>} label="Complete" color="emerald"/>
                    )}
                    {(state === "pending" || state === "in_progress") && (
                      <ActionBtn testid={`tasks-block-${t.id.slice(0,8)}`}
                                 onClick={() => transition(t, "blocked")}
                                 icon={<PauseCircle size={12}/>} label="Block"/>
                    )}
                    {state === "blocked" && (
                      <ActionBtn testid={`tasks-unblock-${t.id.slice(0,8)}`}
                                 onClick={() => transition(t, "in_progress")}
                                 icon={<PlayCircle size={12}/>} label="Resume"/>
                    )}
                    {!isTerminal && (
                      <ActionBtn testid={`tasks-cancel-${t.id.slice(0,8)}`}
                                 onClick={() => transition(t, "cancelled")}
                                 icon={<XCircle size={12}/>} label="Cancel"/>
                    )}
                    <button onClick={() => del(t)}
                            data-testid={`tasks-delete-${t.id.slice(0,8)}`}
                            className="text-slate-400 hover:text-red-600" title="Delete">
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {tasks.length === 0 && (
        <div className="text-center text-sm text-slate-500 py-8" data-testid="tasks-empty">
          No tasks yet — add one above to get started.
        </div>
      )}
    </div>
  );
}

const ActionBtn = ({ onClick, icon, label, color = "slate", testid }) => (
  <button onClick={onClick} data-testid={testid} title={label}
          className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-1 rounded-md border ${
            color === "emerald"
              ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-emerald-500"
              : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
          }`}>
    {icon}<span className="hidden sm:inline">{label}</span>
  </button>
);
